/**
 * CSRF token management utility
 *
 * ARCHITECTURE DECISION:
 * This application uses JWT authentication via Authorization headers, which provides
 * inherent CSRF protection because browsers don't automatically attach custom headers
 * to requests. CSRF tokens are NOT required for security in this configuration.
 *
 * CURRENT STATE:
 * - Backend has CSRF middleware configured but the cookie is httpOnly (can't be read by JS)
 * - This module exists for forward compatibility if CSRF is properly integrated later
 * - All functions gracefully return empty values when no token is available
 *
 * JWT + Authorization Header = CSRF-safe because:
 * - Custom headers (like Authorization) cannot be set by cross-origin requests
 * - Browsers only auto-attach cookies, not custom headers
 * - Attackers cannot forge the Authorization header from a malicious site
 *
 * DO NOT REMOVE without also removing:
 * - Import and usage in services/api.js
 */

const CSRF_TOKEN_KEY = "csrf_token";
const CSRF_HEADER_NAME = "X-CSRF-Token";

/**
 * Get CSRF token from cookie or meta tag
 * Returns null if no token found (expected with JWT-only auth)
 *
 * NOTE: The backend sets httpOnly cookies, so this will typically return null.
 * JWT auth doesn't require CSRF tokens.
 */
export const getCsrfToken = () => {
  // Try to get from meta tag first (common in server-rendered apps)
  const metaTag = document.querySelector('meta[name="csrf-token"]');
  if (metaTag) {
    return metaTag.getAttribute("content");
  }

  // Try to get from cookie (won't work if httpOnly)
  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split("=");
    if (name === CSRF_TOKEN_KEY) {
      return decodeURIComponent(value);
    }
  }

  // No CSRF token found - this is expected with JWT-only auth
  return null;
};

/**
 * Add CSRF token to FormData if available
 * Silently does nothing if no token exists (expected with JWT-only auth)
 */
export const addCsrfToFormData = (formData) => {
  const token = getCsrfToken();
  if (token && formData instanceof FormData) {
    formData.append("_csrf", token);
  }
  return formData;
};

/**
 * Get CSRF header object for axios requests
 * Returns empty object if no token exists (expected with JWT-only auth)
 */
export const getCsrfHeader = () => {
  const token = getCsrfToken();
  return token ? { [CSRF_HEADER_NAME]: token } : {};
};

const csrfUtils = {
  getCsrfToken,
  addCsrfToFormData,
  getCsrfHeader,
  CSRF_HEADER_NAME,
};

export default csrfUtils;
